import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { DbSnapshot, RestoreStoreMessage } from '@libs/src/admin/admin.model';
import { createHttpParamsFromObject } from '@libs/src/http-params/http-params.helper';
import { Account } from '@libs/src/models/account.model';
import {
  ActionItem,
  UpdatePayloadActionItem,
} from '@libs/src/models/action-item.model';
import {
  ActionQueue,
  ImportHistory,
} from '@libs/src/models/action-queue.model';
import { Group } from '@libs/src/models/group.model';
import { UploadCSVUniqueColumn } from '@libs/src/utilities/csv.utilities';
import {
  ParsedRawAccountFromCSVResponse,
  RawAccountFromCSV,
} from '@main-client/src/app/group-edit-members/group-edit-members.interface';
import {
  ColumnData,
  FileColumnMapping,
  ParsedFileMetaData,
} from '@main-client/src/app/invite-csv/attribute-mapping/attribute-mapping-helper';
import { PreregistrationAccount } from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';

const ADMIN_ACCOUNTS_API_URL = '/api/admin/accounts';
const ADMIN_GROUPS_API_URL = '/api/admin/groups';
const ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL =
  'api/v3/automated-user-management';

@Injectable({ providedIn: 'root' })
export class AdminBackend {
  v3 = {
    deleteActionItem: (
      actionItemId: ActionItem['_id'],
      actionQueueId: ActionQueue['_id'],
    ) =>
      this.http.delete<ActionItem>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${actionQueueId}/action-items/${actionItemId}`,
      ),
    downloadEncryptedHRISFile: (
      groupSlug: Group['slug'],
      fileName: ActionQueue['flat_file_name'],
      fileGeneration: ActionQueue['flat_file_generation'],
    ) =>
      this.http.get(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/hris-files/${groupSlug}/${fileName}/${fileGeneration}`,
        { responseType: 'blob' },
      ),
    getAccountsCsvData: (
      groupId: Group['_id'],
      params: { excludeIds: string },
    ) =>
      this.http.get(`api/v3/groups/${groupId}/accounts-csv-data`, {
        params,
      }),
    getActionItems: (id: ActionQueue['_id']) =>
      this.http.get<{ actionItems: ActionItem[] }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${id}/action-items`,
      ),
    getActionQueue: (id: ActionQueue['_id']) =>
      this.http.get<ActionQueue>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${id}`,
      ),
    getActionQueues: (params?: {
      fileName?: ActionQueue['flat_file_name'];
      groupId?: Group['_id'];
      status?: ActionQueue['status'];
    }) =>
      this.http.get<{ actionQueues: ActionQueue[] }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues`,
        { params },
      ),
    getAllActionQueuesAndFiles: () =>
      this.http.get<{ aumImportFileInfo: ImportHistory[] }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/files`,
      ),
    getDbSnapshots: () =>
      this.http.get<DbSnapshot[]>('/api/v3/admin/db/snapshots'),
    getInitialMappingState: (fileId: string, groupId: Group['_id']) =>
      this.http.get<{ data: ColumnData[] }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/bulk-invitations/groups/${groupId}/files/${fileId}/initial-attribute-mapping-state`,
      ),
    patchAccount: (groupId: Group['_id'], update: any) =>
      this.http.patch<Account>(`api/v3/groups/${groupId}/account`, update),
    patchActionItem: (
      actionItemId: ActionItem['_id'],
      actionQueueId: ActionQueue['_id'],
      body: { action: ActionItem['action']; status: ActionItem['status'] },
    ) =>
      this.http.patch<ActionItem>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${actionQueueId}/action-items/${actionItemId}`,
        body,
      ),
    patchActionItemForRevalidate: (
      actionItemId: UpdatePayloadActionItem['_id'],
      actionQueueId: ActionQueue['_id'],
      body: PreregistrationAccount,
    ): Observable<{ actionItem: ActionItem }> =>
      this.http.patch<{ actionItem: ActionItem }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${actionQueueId}/action-items/${actionItemId}/revalidate`,
        body,
      ),
    patchActionItemPayloadTranslated: (
      actionItemId: UpdatePayloadActionItem['_id'],
      actionQueueId: ActionQueue['_id'],
      body: PreregistrationAccount,
    ): Observable<ActionItem> =>
      this.http.patch<ActionItem>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${actionQueueId}/action-items/${actionItemId}/payload-translated`,
        body,
      ),
    patchActionQueue: (
      id: ActionQueue['_id'],
      body: { status: ActionQueue['status'] },
    ) =>
      this.http.patch<ActionQueue>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${id}`,
        body,
      ),
    postActionQueues: (
      columnMappings: FileColumnMapping[],
      fileMetadata: ParsedFileMetaData,
      groupId: Group['_id'],
      isOptedInEmailCampaigns: 'false' | 'true',
    ) =>
      this.http.post<ActionQueue>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${groupId}/prepare-invitations`,
        { columnMappings, fileMetadata, isOptedInEmailCampaigns },
      ),
    postDbRestore: (
      fileName: string,
      skipCurrentUser: boolean,
      groupName: string,
    ) =>
      this.http.post<RestoreStoreMessage>('/api/v3/admin/db/restore', {
        fileName,
        skipCurrentUser,
        ...(groupName && { groupName }),
      }),
    postDbRestoreOptions: (options: {
      options: {
        integrations: { calendar: string };
      };
    }) =>
      this.http.post<{ success: boolean }>(
        '/api/v3/admin/db/restore/options',
        options,
      ),
    postDbStore: (fileName: string) =>
      this.http.post<RestoreStoreMessage>('/api/v3/admin/db/store', {
        fileName,
      }),
    postParseCsv: (data: FormData) =>
      this.http.post<{
        data: UploadCSVUniqueColumn[];
        originalName: string;
      }>(`${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/parse-csv`, data),
    postUpload: (data: FormData) =>
      this.http.post<{ flat_file_generation: string }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/upload`,
        data,
      ),
    processActionQueue: (id: ActionQueue['_id']) =>
      this.http.post<ActionQueue>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/action-queues/${id}/process`,
        {},
      ),
    transformAccountsFromCSV: (
      groupId: Group['_id'],
      data: FormData | { parsedRawAccountsFromCSV: RawAccountFromCSV[] },
    ) =>
      this.http.post<ParsedRawAccountFromCSVResponse>(
        `api/v3/groups/${groupId}/transform-accounts-csv-data`,
        data,
      ),
    uploadCsvFile: (data: FormData) =>
      this.http.post<{
        flat_file_generation: string;
        flat_file_name: ActionQueue['flat_file_name'];
      }>(
        `${ADMIN_AUTOMATED_USER_MANAGEMENT_API_URL}/bulk-invitations/upload-csv`,
        data,
      ),
  };

  constructor(private readonly http: HttpClient) {}

  getAccounts(params: any) {
    return this.http.get(ADMIN_ACCOUNTS_API_URL, { params });
  }

  patchAccount(account: Account, update: any) {
    return this.http.patch(`${ADMIN_ACCOUNTS_API_URL}/${account._id}`, update);
  }

  postAccountSubDocument(
    account: Account,
    subDocumentField: string,
    subDocument: any,
  ) {
    return this.http.post(
      `${ADMIN_ACCOUNTS_API_URL}/${account._id}/${subDocumentField}`,
      subDocument,
    );
  }

  putAccountSubDocument(
    account: Account,
    subDocumentField: string,
    subDocumentId: string,
    subDocumentUpdate: any,
  ) {
    return this.http.put(
      `${ADMIN_ACCOUNTS_API_URL}/${account._id}/${subDocumentField}/${subDocumentId}`,
      subDocumentUpdate,
    );
  }

  deleteAccount(account: Account, params: any = {}) {
    return this.http.delete(`${ADMIN_ACCOUNTS_API_URL}/${account._id}`, {
      params,
    });
  }

  getAccount(account: Account) {
    return this.http.get(`${ADMIN_ACCOUNTS_API_URL}/${account._id}`);
  }

  getGroups({
    keyword = '',
    limit = '100',
    status = [],
    skip = '0',
  }: {
    keyword?: string;
    limit?: string;
    skip?: string;
    status?: Array<Group['status']>;
  } = {}) {
    const params = createHttpParamsFromObject({
      keyword,
      limit,
      skip,
      status,
    });
    return this.http.get<{ count: number; groups: Group[] }>(
      ADMIN_GROUPS_API_URL,
      { params },
    );
  }
}
